/**
 * AssetSource over fetch (browser + worker). OWNER: netcode agent.
 *
 * Shared fetches: in single player the sim worker and the page (prediction) both need
 * collision.bin (9.25 MB) at nearly the same moment, before the HTTP cache is warm, so it was
 * downloaded (and served) twice. The worker now asks the page for its assets (WorkerTransport,
 * `kind: 'asset'`), and the page fetches each URL once: concurrent and shortly-later requests for
 * the same path share one download (`fetchShared`). Each caller gets its own copy of the bytes.
 */
import type { AssetSource } from '@shared/assets';

/** Keep a finished download this long for a second consumer (worker first, page after `load`). */
export const SHARED_FETCH_RETAIN_MS = 60_000;

const shared = new Map<string, Promise<ArrayBuffer>>();

/** Fetch `url` once for all callers within SHARED_FETCH_RETAIN_MS; every caller gets a private copy. */
export function fetchShared(url: string, fetchFn: typeof fetch = fetch): Promise<ArrayBuffer> {
  let p = shared.get(url);
  if (!p) {
    p = fetchFn(url).then((r) => {
      if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
      return r.arrayBuffer();
    });
    shared.set(url, p);
    const entry = p;
    const forget = () => { if (shared.get(url) === entry) shared.delete(url); };
    p.then(() => { setTimeout(forget, SHARED_FETCH_RETAIN_MS); }, forget);
  }
  return p.then((b) => b.slice(0));
}

export function createFetchAssetSource(base = '/assets/'): AssetSource {
  return {
    async json<T>(p: string): Promise<T> {
      const b = await fetchShared(base + p);
      return JSON.parse(new TextDecoder().decode(b)) as T;
    },
    async binary(p: string): Promise<ArrayBuffer> {
      return fetchShared(base + p);
    },
  };
}
